/**
 * The stonecutter: a sturdy low machine on its stone. A slate plinth, a metal gantry holding a
 * round saw that spins while it cuts, the cut blocks stacking up behind it as it stores stone,
 * rough chips at its foot, glowing warm like the stone it draws from, so the stone beneath is plain,
 * and a warm amber lamp on top. It stands
 * well inside its tile: the grey stone shows all round. Offsets are in machine space, y up from the
 * tile's top, a tile about 1.2 wide.
 */
import * as THREE from 'three';

import { around, merged, part, type MachinePart } from './part';

const SLATE = 0x3b4150;
const METAL = 0xd9dee8;
const STEEL = 0xc4cad3;
const SANDSTONE = 0xdcc49a;
/** Its chips: warm and glowing like the lit stones on the ground it draws from (props/catalog.ts). */
const CHIPS = 0xe8cfa6;
const AMBER = 0xffb347;
const AMBER_GLOW = 0xf2952e;

/** The whole machine's size against the measures below: sturdier than the resonator. */
const SIZE = 1.25;
const PLINTH = 0.07;
/** The saw: its radius, and where its axle runs (along x, a little forward). */
const SAW = 0.13;
const AXLE_Y = PLINTH + SAW;
const FRONT = 0.05;
/** The block stack, empty to full: its height at full, at the machine's size. */
export const STACK_FULL = 0.32 * SIZE;

/**
 * A part at the machine's size: its shape and its place scaled together. The block stack keeps its
 * unit height (the props scale it to how full it is, up to STACK_FULL).
 */
function sized(p: MachinePart, unitTall = false): MachinePart {
  p.geometry.scale(SIZE, unitTall ? 1 : SIZE, SIZE);
  return { ...p, offset: [p.offset[0] * SIZE, p.offset[1] * SIZE, p.offset[2] * SIZE] };
}

/** The saw: a disc with teeth round its rim, turning about x. */
function sawDisc(): THREE.BufferGeometry {
  const teeth = Array.from({ length: 16 }, (_, k) =>
    new THREE.BoxGeometry(0.016, 0.028, 0.03)
      .translate(0, SAW + 0.006, 0)
      .rotateX((k * Math.PI) / 8),
  );
  return merged([
    new THREE.CylinderGeometry(SAW, SAW, 0.014, 24).rotateZ(Math.PI / 2),
    new THREE.CylinderGeometry(0.035, 0.035, 0.03, 10).rotateZ(Math.PI / 2),
    ...teeth,
  ]);
}

/** The gantry: two posts beside the saw, its axle, and a beam over it. */
function gantry(): THREE.BufferGeometry {
  const post = (x: number): THREE.BufferGeometry =>
    new THREE.BoxGeometry(0.035, 0.3, 0.05).translate(x, PLINTH + 0.15, FRONT);
  return merged([
    post(-0.085),
    post(0.085),
    new THREE.CylinderGeometry(0.016, 0.016, 0.2, 8)
      .rotateZ(Math.PI / 2)
      .translate(0, AXLE_Y, FRONT),
    new THREE.BoxGeometry(0.22, 0.045, 0.08).translate(0, PLINTH + 0.32, FRONT),
  ]);
}

/** Three cut blocks, stacked a little askew, a unit tall from their foot: scaled to how full it is. */
function blockStack(): THREE.BufferGeometry {
  return merged([
    new THREE.BoxGeometry(0.17, 0.3, 0.13).translate(0, 0.15, 0),
    new THREE.BoxGeometry(0.15, 0.3, 0.12).rotateY(0.18).translate(0.005, 0.485, 0),
    new THREE.BoxGeometry(0.16, 0.3, 0.13).rotateY(-0.12).translate(-0.004, 0.82, 0),
  ]);
}

/** The block stack behind the saw. */
export const STONECUTTER_STACK: MachinePart = sized(
  part('stc.blocks', blockStack(), SANDSTONE, [0, PLINTH, -0.13], {
    roughness: 0.85,
    metalness: 0,
  }),
  true,
);

/** The saw, turning while it cuts. */
export const STONECUTTER_SAW: MachinePart = sized(
  part('stc.saw', sawDisc(), STEEL, [0, AXLE_Y, FRONT], {
    roughness: 0.3,
    metalness: 0.7,
    spin: 'x',
    speed: 7,
  }),
);

/** The parts that stand still. */
export const STONECUTTER_FRAME: readonly MachinePart[] = [
  part('stc.base', new THREE.CylinderGeometry(0.22, 0.26, PLINTH, 6), SLATE, [0, PLINTH / 2, 0], {
    metalness: 0.35,
  }),
  part('stc.gantry', gantry(), METAL, [0, 0, 0], { metalness: 0.45 }),
  part('stc.lamp', new THREE.OctahedronGeometry(0.04), AMBER, [0, PLINTH + 0.39, FRONT], {
    roughness: 0.3,
    glow: AMBER_GLOW,
    pulse: true,
  }),
  part(
    'stc.chips',
    around(
      (k) => new THREE.DodecahedronGeometry(0.058 + (k % 2) * 0.016).scale(1.2, 0.7, 1),
      [0.9, 2.3, 3.9, 5.3],
      0.3,
    ),
    CHIPS,
    [0, 0.035, 0],
    { roughness: 0.6, metalness: 0, glow: AMBER_GLOW, glowStrength: 0.5, pulse: true },
  ),
].map((p) => sized(p));
